import React, { useEffect, useState } from "react";
import "./createAddons.css";
import {
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as DropdownIcon } from "../../../assets/icons/arrow-down.svg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  addAddonAsync,
  editAddonAsync,
  fetchAddonDetailsAsync,
  fetchStatesAsync,
} from "../../../redux/reducers/addonsSlice";
import ApiLoader from "../../../components/apiLoader/apiLoader";
import Toast from "../../../components/toast/toast";
import { adminTexts } from "../../../utils/constants";
import { ReactComponent as BackBtn } from "../../../assets/icons/arrow-down.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/trash.svg";

const CreateAddons = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const { addonDetails, statesData, isAddonAdded, isEditingAddon, isError } =
    useSelector((state) => ({
      addonDetails: state?.addonsData?.selectedAddon,
      statesData: state?.addonsData?.statesData,
      isAddonAdded: state?.addonsData?.isAddingAddon,
      isEditingAddon: state?.addonsData?.isEditingAddon,
      isError: state?.addonsData?.isError,
    }));

  const [toast, setToast] = useState(false);
  const [addonsData, setAddonsData] = useState({
    name: "",
    description: "",
    state_code: "",
    addon_rates: [],
  });
  const [addonsRateData, setAddonsRateData] = useState({
    min_device_price: "",
    max_device_price: "",
    price: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      dispatch(fetchAddonDetailsAsync(id));
    }
    dispatch(fetchStatesAsync());
  }, [id, dispatch]);

  useEffect(() => {
    if (addonDetails && id) {
      setAddonsData({
        name: addonDetails.name || "",
        description: addonDetails.description || "",
        state_code: addonDetails.state_code || "",
        addon_rates: addonDetails.addonRates
          ? addonDetails.addonRates.map((rate) => ({
              id: rate.id,
              min_device_price: rate.min_device_price.toString(),
              max_device_price: rate.max_device_price.toString(),
              price: rate.price.toString(),
            }))
          : [],
      });
    }
  }, [addonDetails, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddonsData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRateChange = (e) => {
    const { name, value } = e.target;

    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setAddonsRateData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEditRateChange = (index, field, value) => {
    if (addonsData?.addon_rates[index]?.id) {
      const updatedRates = [...addonsData.addon_rates];
      updatedRates[index][field] = value;

      if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
        setAddonsData({
          ...addonsData,
          addon_rates: updatedRates,
        });
      }
    }
  };

  const handleDeleteRate = (index) => {
    setAddonsData((prev) => ({
      ...prev,
      addon_rates: prev.addon_rates.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    let isValid = true;
    let validationErrors = {};

    // Validate general fields
    Object.keys(addonsData)?.forEach((field) => {
      if (
        !addonsData[field] &&
        field !== "addonsRateData" &&
        field !== "state_code" &&
        field !== "description"
      ) {
        validationErrors[field] = "This field is required";
        isValid = false;
      }
    });

    // If there's no ID (i.e., creating a new addon), check if addonsRateData have all required fields
    if (!id) {
      addonsData?.addonsRateData?.forEach((rate, index) => {
        if (!rate.min_device_price || !rate.max_device_price || !rate.price) {
          validationErrors[`rate_${index}`] = "All rate fields are required";
          isValid = false;
        }
      });

      // Ensure there is at least one addon rate when creating a new addon
      if (addonsData?.addon_rates?.length === 0) {
        validationErrors.addon_rates = "At least one price field must be added";
        isValid = false;
      }
    }

    setErrors(validationErrors);
    return isValid;
  };

  const validateRate = () => {
    let isValid = true;
    let validationErrors = {};
    setErrors({});

    Object.keys(addonsRateData).forEach((field) => {
      if (!addonsRateData[field]) {
        validationErrors[field] = "This field is required";
        isValid = false;
      }
    });

    if (
      addonsRateData.min_device_price &&
      addonsRateData.max_device_price &&
      parseInt(addonsRateData.min_device_price) >=
        parseInt(addonsRateData.max_device_price)
    ) {
      validationErrors.min_device_price =
        "Min device price should be less than Max device price";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...validationErrors }));
    return isValid;
  };

  const handleAddPlanRate = () => {
    if (validateRate()) {
      setAddonsData((prev) => ({
        ...prev,
        addon_rates: [
          ...(prev.addon_rates || []),
          {
            min_device_price: parseInt(addonsRateData.min_device_price),
            max_device_price: parseInt(addonsRateData.max_device_price),
            price: parseFloat(addonsRateData.price).toFixed(2),
          },
        ],
      }));
      setAddonsRateData({
        min_device_price: "",
        max_device_price: "",
        price: "",
      });
    }
  };

  const handleSubmitPlan = () => {
    setToast(false);
    if (validateForm()) {
      const filteredAddonRates = addonsData?.addon_rates?.filter(
        (rate) => rate.min_line || rate.max_line || rate.price
      );

      const updatedAddonData = {
        ...addonsData,
        addon_rates: filteredAddonRates,
      };
      if (addonsData?.addon_rates?.length > 0 && id) {
        dispatch(editAddonAsync({ id, updatedData: updatedAddonData })).then(
          (res) => {
            setToast({ header: res?.payload?.message });
            dispatch(fetchAddonDetailsAsync(id));
          }
        );
      } else if (addonsData?.addon_rates?.length > 0) {
        dispatch(addAddonAsync(addonsData)).then((res) => {
          setToast({ header: res?.payload?.message });
        });
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    // Reset form data and errors after successful submission
    if (!id && isAddonAdded) {
      setAddonsData({
        name: "",
        description: "",
        state_code: "",
        addon_rates: [],
      });

      setAddonsRateData({
        min_device_price: "",
        max_device_price: "",
        price: "",
      });
    }
  }, [isAddonAdded, id]);

  const addonDetailsFields = [
    {
      label: "Name",
      name: "name",
      placeholder: "Enter addon name",
      type: "text",
      maxLength: 50,
    },
  ];

  const addPlanFields = [
    {
      label: "Min device price",
      name: "min_device_price",
      placeholder: "Min device price",
      type: "number",
    },
    {
      label: "Max device price",
      name: "max_device_price",
      placeholder: "Max device price",
      type: "number",
    },
    { label: "Price", name: "price", placeholder: "Price", type: "number" },
  ];

  return (
    <div className="create-plan-container">
      {/* <ApiLoader loading={!isAddonAdded} /> */}
      <div className="previous-page" onClick={handleBack}>
        <BackBtn className="back-btn" />
        <p>Back</p>
      </div>
      <h1 className="title">{id ? "Edit Addon" : "Create Addon"}</h1>
      {/* Plan Details Form */}
      <div className="form-grid addon-details">
        {addonDetailsFields.map((field, index) => (
          <div key={index} className="field-group">
            <label className="input-label">{field.label}</label>
            <input
              type={field.type}
              name={field.name}
              value={addonsData[field.name] || ""}
              onChange={handleInputChange}
              placeholder={field.placeholder}
              className="input"
              maxLength={field?.maxLength}
            />
            {errors[field.name] && (
              <FormHelperText className="error-text" error>
                {errors[field.name]}
              </FormHelperText>
            )}
          </div>
        ))}

        <FormControl fullWidth className="custom-select-container">
          <label id="state-code-label">Select State</label>
          <Select
            labelId="state-code-label"
            name="state_code"
            value={addonsData.state_code}
            onChange={handleInputChange}
            className="input custom-select"
            IconComponent={() => <DropdownIcon />}
            displayEmpty
            renderValue={(selected) => {
              const selectedState = statesData?.find(
                (state) => state.stateCode === selected
              );
              return selectedState ? (
                selectedState.stateName
              ) : (
                <span className="placeholder">Select State</span>
              );
            }}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {statesData?.map((state) => (
              <MenuItem key={state.stateCode} value={state.stateCode}>
                {state.stateName}
              </MenuItem>
            ))}
          </Select>
          {errors.device_type && (
            <FormHelperText className="error-text" error>
              {errors.device_type}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl
          fullWidth
          className="textarea-container"
          error={Boolean(errors.description)}
        >
          <label className="input-label">Description</label>
          <textarea
            name="description"
            maxLength={250}
            placeholder="Enter description here"
            rows={5}
            className="addon-textarea"
            value={addonsData.description}
            onChange={handleInputChange}
          />
          {errors.description && (
            <FormHelperText sx={{ marginLeft: "0" }}>
              {errors.description}
            </FormHelperText>
          )}
        </FormControl>
      </div>

      {/* Add Plans Form */}
      <div className="form-grid add-plan">
        {addPlanFields.map((field, index) => (
          <div key={index} className="field-group">
            <label className="input-label">{field.label}</label>
            <input
              type={field.type}
              name={field.name}
              value={addonsRateData[field.name]}
              onChange={handleRateChange}
              placeholder={field.placeholder}
              className="input"
            />
            {errors[field.name] && (
              <FormHelperText className="error-text" error>
                {errors[field.name]}
              </FormHelperText>
            )}
          </div>
        ))}
        <button onClick={handleAddPlanRate} className="add-plan-button">
          Add Price <PlusIcon />
        </button>
      </div>

      {/* Plans List */}
      <div className="plans-list">
        {addonsData?.addon_rates?.map((addon, index) => {
          return (
            <div key={index} className="plan-item plan-added-item">
              <div className="field-group plan-added-group">
                <label className="input-label">Min device price</label>
                <input
                  className="plan-value"
                  value={addon.min_device_price}
                  onChange={(e) =>
                    handleEditRateChange(
                      index,
                      "min_device_price",
                      e.target.value
                    )
                  }
                  readOnly={!addon.id}
                />
              </div>
              <div className="field-group plan-added-group">
                <label className="input-label">Max device price</label>
                <input
                  className="plan-value"
                  value={addon.max_device_price}
                  onChange={(e) =>
                    handleEditRateChange(
                      index,
                      "max_device_price",
                      e.target.value
                    )
                  }
                  readOnly={!addon.id}
                />
              </div>
              <div className="field-group plan-added-group">
                <label className="input-label">Price</label>
                <input
                  className="plan-value"
                  value={addon.price}
                  onChange={(e) =>
                    handleEditRateChange(index, "price", e.target.value)
                  }
                  readOnly={!addon.id}
                />
              </div>
              <IconButton
                onClick={() => handleDeleteRate(index)}
                className="delete-plan-button"
              >
                <DeleteIcon />
              </IconButton>
            </div>
          );
        })}
      </div>
      {toast && !isError && <Toast toast={toast} />}
      <button onClick={handleSubmitPlan} className="add-plan-button">
        Submit
      </button>
      {isError && <p className="error">{adminTexts.error_message} </p>}
      {errors?.addon_rates && (
        <div className="error">{errors?.addon_rates}</div>
      )}
    </div>
  );
};

export default CreateAddons;
